/**
 * Stack trace parsing and source map decoding utilities
 * Helps decode minified stack traces for better debugging
 */

export interface StackFrame {
  functionName: string | null;
  fileName: string | null;
  lineNumber: number | null;
  columnNumber: number | null;
  source: string;
}

/**
 * Parse a stack trace string into structured frames
 */
export function parseStackTrace(stack: string): StackFrame[] {
  if (!stack) return [];
  
  const lines = stack.split('\n');
  const frames: StackFrame[] = [];
  
  for (const line of lines) {
    const frame = parseStackFrame(line.trim());
    if (frame) {
      frames.push(frame);
    }
  }
  
  return frames;
}

/**
 * Parse a single stack frame line
 * Handles various formats:
 * - Chrome/V8: "at functionName (file:line:column)"
 * - Firefox: "functionName@file:line:column"
 * - Safari: "functionName@file:line:column"
 */
function parseStackFrame(line: string): StackFrame | null {
  // Skip empty lines or non-frame lines
  if (!line || line.startsWith('Error:') || !line.includes(':')) {
    return null;
  }
  
  // Chrome/V8 format: "at functionName (file:line:column)" or "at file:line:column"
  const chromeMatch = line.match(/^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
  if (chromeMatch) {
    return {
      functionName: chromeMatch[1] || null,
      fileName: chromeMatch[2],
      lineNumber: parseInt(chromeMatch[3], 10),
      columnNumber: parseInt(chromeMatch[4], 10),
      source: line,
    };
  }
  
  // Firefox/Safari format: "functionName@file:line:column"
  const firefoxMatch = line.match(/^(.+?)@(.+?):(\d+):(\d+)$/);
  if (firefoxMatch) {
    return {
      functionName: firefoxMatch[1] || null,
      fileName: firefoxMatch[2],
      lineNumber: parseInt(firefoxMatch[3], 10),
      columnNumber: parseInt(firefoxMatch[4], 10),
      source: line,
    };
  }
  
  // Simple format: "file:line:column"
  const simpleMatch = line.match(/^(.+?):(\d+):(\d+)$/);
  if (simpleMatch) {
    return {
      functionName: null,
      fileName: simpleMatch[1],
      lineNumber: parseInt(simpleMatch[2], 10),
      columnNumber: parseInt(simpleMatch[3], 10),
      source: line,
    };
  }
  
  return null;
}

/**
 * Format stack frames back into a readable string
 */
export function formatStackFrames(frames: StackFrame[]): string {
  return frames
    .map((frame) => {
      const fn = frame.functionName || '<anonymous>';
      const file = frame.fileName || '<unknown>';
      const line = frame.lineNumber || '?';
      const col = frame.columnNumber || '?';
      return `  at ${fn} (${file}:${line}:${col})`;
    })
    .join('\n');
}

/**
 * Extract chunk/module info from minified filenames
 */
export function extractBuildInfo(fileName: string | null): {
  chunk: string | null;
  hash: string | null;
  isVendor: boolean;
} {
  if (!fileName) {
    return { chunk: null, hash: null, isVendor: false };
  }
  
  // Match patterns like "index-abc123.js" or "vendor-xyz789.js"
  const match = fileName.match(/([^/]+)-([a-f0-9]{8})\.js$/i);
  if (match) {
    return {
      chunk: match[1],
      hash: match[2],
      isVendor: match[1].toLowerCase() === 'vendor',
    };
  }
  
  return { chunk: null, hash: null, isVendor: false };
}

/**
 * Enrich stack trace with additional context
 */
export function enrichStackTrace(stack: string): {
  frames: StackFrame[];
  formatted: string;
  summary: string;
  appFrames: StackFrame[];
  vendorFrames: StackFrame[];
} {
  const frames = parseStackTrace(stack);
  
  const appFrames: StackFrame[] = [];
  const vendorFrames: StackFrame[] = [];
  
  for (const frame of frames) {
    const buildInfo = extractBuildInfo(frame.fileName);
    if (buildInfo.isVendor || frame.fileName?.includes('node_modules')) {
      vendorFrames.push(frame);
    } else {
      appFrames.push(frame);
    }
  }
  
  // Create summary from first app frame
  const firstAppFrame = appFrames[0];
  let summary = 'Unknown location';
  if (firstAppFrame) {
    const fn = firstAppFrame.functionName || 'anonymous';
    const file = firstAppFrame.fileName?.split('/').pop() || 'unknown';
    summary = `${fn} in ${file}:${firstAppFrame.lineNumber}`;
  }
  
  return {
    frames,
    formatted: formatStackFrames(frames),
    summary,
    appFrames,
    vendorFrames,
  };
}

/**
 * Get source map URL for a given script
 */
export async function fetchSourceMapUrl(scriptUrl: string): Promise<string | null> {
  try {
    const response = await fetch(scriptUrl);
    const text = await response.text();
    
    // Look for sourceMappingURL comment
    const match = text.match(/\/\/[#@]\s*sourceMappingURL=(.+?)(\s|$)/);
    if (match) {
      const mapUrl = match[1];
      // Handle relative URLs
      if (!mapUrl.startsWith('http')) {
        const base = new URL(scriptUrl);
        return new URL(mapUrl, base).href;
      }
      return mapUrl;
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Create a detailed error report with parsed stack trace
 */
export function createErrorReport(error: Error, componentStack?: string | null): {
  message: string;
  stack: string | null;
  stackFrames: StackFrame[];
  componentStack: string | null;
  summary: string;
  timestamp: string;
  url: string;
  userAgent: string;
} {
  const enriched = error.stack ? enrichStackTrace(error.stack) : null;
  
  return {
    message: error.message,
    stack: error.stack || null,
    stackFrames: enriched?.frames || [],
    componentStack: componentStack || null,
    summary: enriched?.summary || error.message.slice(0, 100),
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}
